// src/app.js — bundled to public/js/app.bundle.js by `npm run build` (esbuild).
// Page rendering (title, meta, canonical, JSON-LD, h1, FAQ copy) is SSR —
// done server-side in functions/_lib/render.js via HTMLRewriter, before
// this file ever runs. Everything below is CSR: reading the SSR-injected
// bootstrap data and doing the actual hashing entirely in the browser.
//
// Decrypt/reverse-lookup was intentionally removed: hashing is one-way
// and this site never stored anything to look values up against, so a
// "decrypt" feature could only ever fake it with a tiny wordlist. The UI
// now offers three honest modes instead: Hash Input, Verify Hash, and
// Upload File.

import {
    md4, md5, sha1, sha224, sha256, sha384, sha512,
    sha3, keccak, ripemd160, whirlpool, sm3,
    blake2b, blake2s, blake3,
    crc32, crc64, adler32, xxhash64,
    bcrypt, argon2id, argon2i, argon2d, pbkdf2,
    createSHA1, createSHA256, createSHA512, createMD5, createHMAC,
} from 'hash-wasm';

// ---------------------------------------------------------------------
// Bootstrap data injected server-side into #app-data (see render.js).
// Falls back to fetching the JSON directly for local static preview.
// ---------------------------------------------------------------------
const bootEl = document.getElementById('app-data');
let BOOT = null;
try { BOOT = JSON.parse(bootEl.textContent); } catch (e) { BOOT = null; }

async function getBoot() {
    if (BOOT && BOOT.cfg) return BOOT;
    const slug = location.pathname.split('/').filter(Boolean).pop() || 'hash-generator';
    const res = await fetch('/data/hash-tools-config.json');
    const CONFIG = await res.json();
    const cfg = CONFIG[slug] || CONFIG['hash-generator'];
    return { slug, algo: 'sha256', mode: 'generate', cfg };
}

// ---------------------------------------------------------------------
// Algorithms with no maintained WASM/JS library found (verified against
// hash-wasm@4's actual exports, not assumed). Their pages still render
// fully for SEO — this only affects the compute button.
// ---------------------------------------------------------------------
const UNSUPPORTED = new Set(['md2', 'tiger192', 'gost3411', 'haval256', 'skein256', 'skein512']);

// hash-wasm does not ship CRC32C or MurmurHash3 (verified against its
// type defs) or CRC16/FNV-1a (never did). Small compliant pure-JS
// implementations for those four only.
function fnv1a32(str) {
    let h = 0x811c9dc5;
    for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 0x01000193); }
    return (h >>> 0).toString(16).padStart(8, '0');
}
function fnv1a64(str) {
    let h = 0xcbf29ce484222325n;
    const prime = 0x100000001b3n;
    for (let i = 0; i < str.length; i++) { h ^= BigInt(str.charCodeAt(i)); h = BigInt.asUintN(64, h * prime); }
    return h.toString(16).padStart(16, '0');
}
function crc16(str) {
    let crc = 0xFFFF;
    for (let i = 0; i < str.length; i++) {
        crc ^= str.charCodeAt(i);
        for (let j = 0; j < 8; j++) crc = (crc & 1) ? ((crc >> 1) ^ 0xA001) : (crc >> 1);
    }
    return (crc & 0xFFFF).toString(16).padStart(4, '0');
}
const CRC32C_TABLE = (() => {
    const table = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
        let c = n;
        for (let k = 0; k < 8; k++) c = (c & 1) ? (0x82F63B78 ^ (c >>> 1)) : (c >>> 1);
        table[n] = c >>> 0;
    }
    return table;
})();
function crc32c(bytes) {
    let crc = 0xFFFFFFFF;
    for (let i = 0; i < bytes.length; i++) crc = CRC32C_TABLE[(crc ^ bytes[i]) & 0xFF] ^ (crc >>> 8);
    return ((crc ^ 0xFFFFFFFF) >>> 0).toString(16).padStart(8, '0');
}
function murmur3_32(str, seed = 0) {
    let h = seed >>> 0;
    const c1 = 0xcc9e2d51, c2 = 0x1b873593;
    let i = 0;
    const len = str.length;
    while (i + 4 <= len) {
        let k = (str.charCodeAt(i) & 0xff) | ((str.charCodeAt(i + 1) & 0xff) << 8) |
                ((str.charCodeAt(i + 2) & 0xff) << 16) | ((str.charCodeAt(i + 3) & 0xff) << 24);
        i += 4;
        k = Math.imul(k, c1); k = (k << 15) | (k >>> 17); k = Math.imul(k, c2);
        h ^= k; h = (h << 13) | (h >>> 19); h = (Math.imul(h, 5) + 0xe6546b64) >>> 0;
    }
    let k = 0;
    const rem = len - i;
    if (rem === 3) k ^= (str.charCodeAt(i + 2) & 0xff) << 16;
    if (rem >= 2) k ^= (str.charCodeAt(i + 1) & 0xff) << 8;
    if (rem >= 1) { k ^= (str.charCodeAt(i) & 0xff); k = Math.imul(k, c1); k = (k << 15) | (k >>> 17); k = Math.imul(k, c2); h ^= k; }
    h ^= len;
    h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b) >>> 0;
    h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35) >>> 0;
    h ^= h >>> 16;
    return (h >>> 0).toString(16).padStart(8, '0');
}

async function computeHash(algo, input, isFile, secretKey) {
    if (UNSUPPORTED.has(algo)) {
        throw new Error('This algorithm has no maintained browser library \u2014 the page content is still here, hashing isn\u2019t available for it yet.');
    }
    const data = isFile ? new Uint8Array(input) : input;
    const str = isFile ? null : String(input);
    const key = secretKey || 'change-me';

    switch (algo) {
        case 'fnv1a32': return fnv1a32(String(input));
        case 'fnv1a64': return fnv1a64(String(input));
        case 'crc16': return crc16(String(input));
        case 'crc32c': return crc32c(isFile ? data : new TextEncoder().encode(str));
        case 'murmur3': return murmur3_32(String(input));

        case 'md4': return md4(data);
        case 'md5': return md5(data);
        case 'sha1': return sha1(data);
        case 'sha224': return sha224(data);
        case 'sha256': return sha256(data);
        case 'sha384': return sha384(data);
        case 'sha512': return sha512(data);
        case 'sha3-256': return sha3(data, 256);
        case 'sha3-512': return sha3(data, 512);
        case 'keccak-256': return keccak(data, 256);
        case 'keccak-512': return keccak(data, 512);
        case 'ripemd160': return ripemd160(data);
        case 'whirlpool': return whirlpool(data);
        case 'sm3': return sm3(data);
        case 'blake2b': return blake2b(data, 512);
        case 'blake2s': return blake2s(data, 256);
        case 'blake3': return blake3(data);
        case 'crc32': return crc32(data);
        case 'crc64': return crc64(data);
        case 'adler32': return adler32(data);
        case 'xxhash64': return xxhash64(data);

        case 'bcrypt':
            return bcrypt({
                password: str, salt: crypto.getRandomValues(new Uint8Array(16)),
                costFactor: 10, outputType: 'encoded',
            });
        case 'argon2id':
        case 'argon2i':
        case 'argon2d': {
            const fn = { argon2id, argon2i, argon2d }[algo];
            return fn({
                password: str, salt: crypto.getRandomValues(new Uint8Array(16)),
                parallelism: 4, iterations: 3, memorySize: 65536, hashLength: 32,
                outputType: 'encoded',
            });
        }
        case 'pbkdf2-sha256':
            return pbkdf2({ password: str, salt: crypto.getRandomValues(new Uint8Array(16)), iterations: 100000, hashLength: 32, hashFunction: createSHA256(), outputType: 'hex' });
        case 'pbkdf2-sha512':
            return pbkdf2({ password: str, salt: crypto.getRandomValues(new Uint8Array(16)), iterations: 100000, hashLength: 64, hashFunction: createSHA512(), outputType: 'hex' });
        case 'pbkdf2-sha1':
            return pbkdf2({ password: str, salt: crypto.getRandomValues(new Uint8Array(16)), iterations: 100000, hashLength: 20, hashFunction: createSHA1(), outputType: 'hex' });

        case 'hmac-sha256': { const h = await createHMAC(await createSHA256(), key); h.init(); h.update(data); return h.digest(); }
        case 'hmac-sha512': { const h = await createHMAC(await createSHA512(), key); h.init(); h.update(data); return h.digest(); }
        case 'hmac-sha1': { const h = await createHMAC(await createSHA1(), key); h.init(); h.update(data); return h.digest(); }
        case 'hmac-md5': { const h = await createHMAC(await createMD5(), key); h.init(); h.update(data); return h.digest(); }

        default: return sha256(data);
    }
}

// ---------------------------------------------------------------------
// UI wiring
// ---------------------------------------------------------------------
function renderFaqs(faqs) {
    const section = document.getElementById('faq-section');
    const list = document.getElementById('faq-list');
    if (!faqs || !faqs.length) { section.hidden = true; return; }
    list.innerHTML = faqs.map(f => `
        <details class="faq-item">
            <summary>${f.q}</summary>
            <p>${f.a}</p>
        </details>
    `).join('');
    section.hidden = false;
}

async function main() {
    const { algo, cfg } = await getBoot();
    const isHmac = algo.startsWith('hmac-');

    document.getElementById('intro').textContent = cfg.intro || '';
    document.getElementById('year').textContent = new Date().getFullYear();
    renderFaqs(cfg.faqs);

    // Per-panel labels/algorithm badges/placeholders, so every mode reads
    // like it belongs to the same tool.
    document.getElementById('algo-label').textContent = cfg.algoLabel || algo;
    document.getElementById('verify-algo-label').textContent = cfg.algoLabel || algo;
    document.getElementById('file-algo-label').textContent = cfg.algoLabel || algo;
    document.getElementById('input-text').placeholder = cfg.placeholder || 'Enter text';
    document.getElementById('verify-text').placeholder = cfg.placeholder || 'Enter the original text';
    document.querySelector('label[for="output"]').textContent = cfg.outputLabel || 'Output';
    document.querySelector('label[for="file-output"]').textContent = cfg.outputLabel || 'Output';

    document.getElementById('secret-key-field-hash').hidden = !isHmac;
    document.getElementById('secret-key-field-verify').hidden = !isHmac;
    document.getElementById('secret-key-field-file').hidden = !isHmac;

    // ---- Mode switcher: Hash Input (default) / Verify Hash / Upload File ----
    const tabs = {
        hash: document.getElementById('mode-hash'),
        verify: document.getElementById('mode-verify'),
        file: document.getElementById('mode-file'),
    };
    const panels = {
        hash: document.getElementById('panel-hash'),
        verify: document.getElementById('panel-verify'),
        file: document.getElementById('panel-file'),
    };
    function setMode(mode) {
        for (const key of Object.keys(tabs)) {
            const active = key === mode;
            tabs[key].classList.toggle('active', active);
            tabs[key].setAttribute('aria-selected', String(active));
            panels[key].classList.toggle('visible', active);
        }
    }
    tabs.hash.addEventListener('click', () => setMode('hash'));
    tabs.verify.addEventListener('click', () => setMode('verify'));
    tabs.file.addEventListener('click', () => setMode('file'));
    setMode('hash');

    // ---- Panel 1: Hash Input ----
    const textEl = document.getElementById('input-text');
    const secretKeyEl = document.getElementById('secret-key');
    const outputEl = document.getElementById('output');
    const statusEl = document.getElementById('status');
    let hashTimer;
    async function runHash() {
        statusEl.textContent = 'Computing\u2026';
        outputEl.value = '';
        try {
            outputEl.value = textEl.value === '' ? '' : await computeHash(algo, textEl.value, false, secretKeyEl.value);
            statusEl.textContent = '';
        } catch (err) {
            statusEl.textContent = err.message;
        }
    }
    textEl.addEventListener('input', () => { clearTimeout(hashTimer); hashTimer = setTimeout(runHash, 250); });
    secretKeyEl.addEventListener('input', () => { clearTimeout(hashTimer); hashTimer = setTimeout(runHash, 250); });
    document.getElementById('copy-btn').addEventListener('click', () => {
        if (!outputEl.value) return;
        navigator.clipboard.writeText(outputEl.value);
        statusEl.textContent = 'Copied!';
        setTimeout(() => { statusEl.textContent = ''; }, 1200);
    });

    // ---- Panel 2: Verify Hash — recompute and compare, no storage involved ----
    const verifyTextEl = document.getElementById('verify-text');
    const verifySecretKeyEl = document.getElementById('verify-secret-key');
    const verifyHashEl = document.getElementById('verify-hash');
    const verifyResultEl = document.getElementById('verify-result');
    const verifyStatusEl = document.getElementById('verify-status');
    let verifyTimer;
    async function runVerify() {
        const expected = verifyHashEl.value.trim();
        verifyResultEl.classList.remove('show', 'match', 'mismatch');
        verifyStatusEl.textContent = '';
        if (!verifyTextEl.value || !expected) return;
        verifyStatusEl.textContent = 'Checking\u2026';
        try {
            const computed = await computeHash(algo, verifyTextEl.value, false, verifySecretKeyEl.value);
            verifyStatusEl.textContent = '';
            const isMatch = String(computed).toLowerCase() === expected.toLowerCase();
            verifyResultEl.textContent = isMatch
                ? '\u2713 Match \u2014 the hash corresponds to this input.'
                : '\u2717 No match \u2014 the computed hash is different.';
            verifyResultEl.classList.add('show', isMatch ? 'match' : 'mismatch');
        } catch (err) {
            verifyStatusEl.textContent = err.message;
        }
    }
    [verifyTextEl, verifySecretKeyEl, verifyHashEl].forEach(el => {
        el.addEventListener('input', () => { clearTimeout(verifyTimer); verifyTimer = setTimeout(runVerify, 250); });
    });

    // ---- Panel 3: Upload File ----
    const dropZone = document.getElementById('drop-zone');
    const fileInput = document.getElementById('input-file');
    const fileNameEl = document.getElementById('file-name');
    const fileSecretKeyEl = document.getElementById('file-secret-key');
    const fileOutputEl = document.getElementById('file-output');
    const fileStatusEl = document.getElementById('file-status');

    async function runFile(file) {
        if (!file) return;
        fileNameEl.textContent = file.name;
        fileStatusEl.textContent = 'Computing\u2026';
        fileOutputEl.value = '';
        try {
            const buf = await file.arrayBuffer();
            fileOutputEl.value = await computeHash(algo, buf, true, fileSecretKeyEl.value);
            fileStatusEl.textContent = '';
        } catch (err) {
            fileStatusEl.textContent = err.message;
        }
    }
    dropZone.addEventListener('click', () => fileInput.click());
    fileInput.addEventListener('change', () => runFile(fileInput.files[0]));
    fileSecretKeyEl.addEventListener('input', () => { if (fileInput.files[0]) runFile(fileInput.files[0]); });
    ['dragenter', 'dragover'].forEach(evt => {
        dropZone.addEventListener(evt, (e) => { e.preventDefault(); dropZone.classList.add('dragover'); });
    });
    ['dragleave', 'drop'].forEach(evt => {
        dropZone.addEventListener(evt, (e) => { e.preventDefault(); dropZone.classList.remove('dragover'); });
    });
    dropZone.addEventListener('drop', (e) => {
        const file = e.dataTransfer.files[0];
        if (file) { fileInput.files = e.dataTransfer.files; runFile(file); }
    });
    document.getElementById('file-copy-btn').addEventListener('click', () => {
        if (!fileOutputEl.value) return;
        navigator.clipboard.writeText(fileOutputEl.value);
        fileStatusEl.textContent = 'Copied!';
        setTimeout(() => { fileStatusEl.textContent = ''; }, 1200);
    });
}

main();
